/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

export interface LearnMoreLink {
  content: string;
  url: string;
}

export interface Hint {
  description?: string;
  sources?: string;
  citation?: string;
  license?: string;
}

export interface DescriptionFields extends ExtraMetadata {
  description?: string;
  readMoreLinks?: LearnMoreLink[];
  downloadLinks?: LearnMoreLink[];
  sources?: string;
  citation?: string;
  license?: string;
  disclaimer?: string;
}

export interface PartialDate {
  year?: number;
  month?: number;
  day?: number;
}

export interface PointOfContact {
  fullName?: string;
  organization?: string;
  email?: string;
}

export interface ExtraMetadata {
  resolutionMeters?: number;
  dataVersion?: string;
  startDate?: PartialDate;
  endDate?: PartialDate;
  publicationDate?: PartialDate;
}

// Layer taxonomy shared by the map and the admin. Category values are full
// paths: the UI renders them a segment at a time, translating each one.
export enum LayerCategory {
  BOUNDARIES = "Boundaries",
  ADMIN_AREAS = "Boundaries/Administrative Areas",
  BIOGEOGRAPHIC_REGIONS = "Boundaries/Biogeographical Regions",
  PROTECTED_CONSERVED_AREAS = "Protected and Conserved Areas",
  LAND_COVER_AND_USE = "Land Cover and Land Use",
  WATER = "Water",
  WATER_FRESH = "Water/Freshwater",
  WATER_OCEANS = "Water/Oceans",
  CLIMATE_AND_CARBON = "Climate and Carbon",
  CLIMATE_CHANGE_MITIGATION = "Climate and Carbon/Climate Change Mitigation",
  CLIMATE_CHANGE_ADAPTATION = "Climate and Carbon/Climate Change Adaptation",
  NATURE_BASED_SOLUTIONS = "Nature Based Solutions",
  SUSTAINABLE_DEVELOPMENT = "Sustainable Development",
  NATURAL_HAZARDS = "Natural Hazards",
  BIODIVERSITY = "Biodiversity",
  BIODIVERSITY_SPECIES = "Biodiversity/Species",
  BIODIVERSITY_ECOSYSTEMS = "Biodiversity/Ecosystem",
  BIODIVERSITY_GENES = "Biodiversity/Genes",
  ECOSYSTEM_SERVICES = "Ecosystem Services",
  HUMAN_IMPACT = "Human Impact",
  AGRICULTURE = "Agriculture",
  BUILT_ENVIRONMENT = "Built Environment",
  RESTORATION = "Restoration",
  SOCIETY = "Society",
  SOCIOECONOMIC = "Society/Socioeconomic",
  PUBLIC_HEALTH = "Society/Public Health",
  SOCIAL_BEHAVIOR = "Society/Social Behaviour",

  POLICY = "Policy",
  KM_GBF = "Policy/Global Biodiversity Framework",

  KM_GBF_GOAL_A = "Policy/Global Biodiversity Framework/Goal A",
  KM_GBF_GOAL_A_HEADLINE = "Policy/Global Biodiversity Framework/Goal A/Headline Indicators",
  KM_GBF_GOAL_A_COMPONENT = "Policy/Global Biodiversity Framework/Goal A/Component Indicators",
  KM_GBF_GOAL_A_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Goal A/Complementary Indicators",

  KM_GBF_GOAL_B = "Policy/Global Biodiversity Framework/Goal B",
  KM_GBF_GOAL_B_HEADLINE = "Policy/Global Biodiversity Framework/Goal B/Headline Indicators",
  KM_GBF_GOAL_B_COMPONENT = "Policy/Global Biodiversity Framework/Goal B/Component Indicators",
  KM_GBF_GOAL_B_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Goal B/Complementary Indicators",

  KM_GBF_GOAL_C = "Policy/Global Biodiversity Framework/Goal C",
  KM_GBF_GOAL_C_HEADLINE = "Policy/Global Biodiversity Framework/Goal C/Headline Indicators",
  KM_GBF_GOAL_C_COMPONENT = "Policy/Global Biodiversity Framework/Goal C/Component Indicators",
  KM_GBF_GOAL_C_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Goal C/Complementary Indicators",

  KM_GBF_GOAL_D = "Policy/Global Biodiversity Framework/Goal D",
  KM_GBF_GOAL_D_HEADLINE = "Policy/Global Biodiversity Framework/Goal D/Headline Indicators",
  KM_GBF_GOAL_D_COMPONENT = "Policy/Global Biodiversity Framework/Goal D/Component Indicators",
  KM_GBF_GOAL_D_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Goal D/Complementary Indicators",

  KM_GBF_TARGET_1 = "Policy/Global Biodiversity Framework/Target 01",
  KM_GBF_TARGET_1_HEADLINE = "Policy/Global Biodiversity Framework/Target 01/Headline Indicators",
  KM_GBF_TARGET_1_COMPONENT = "Policy/Global Biodiversity Framework/Target 01/Component Indicators",
  KM_GBF_TARGET_1_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 01/Complementary Indicators",

  KM_GBF_TARGET_2 = "Policy/Global Biodiversity Framework/Target 02",
  KM_GBF_TARGET_2_HEADLINE = "Policy/Global Biodiversity Framework/Target 02/Headline Indicators",
  KM_GBF_TARGET_2_COMPONENT = "Policy/Global Biodiversity Framework/Target 02/Component Indicators",
  KM_GBF_TARGET_2_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 02/Complementary Indicators",

  KM_GBF_TARGET_3 = "Policy/Global Biodiversity Framework/Target 03",
  KM_GBF_TARGET_3_HEADLINE = "Policy/Global Biodiversity Framework/Target 03/Headline Indicators",
  KM_GBF_TARGET_3_COMPONENT = "Policy/Global Biodiversity Framework/Target 03/Component Indicators",
  KM_GBF_TARGET_3_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 03/Complementary Indicators",

  KM_GBF_TARGET_4 = "Policy/Global Biodiversity Framework/Target 04",
  KM_GBF_TARGET_4_HEADLINE = "Policy/Global Biodiversity Framework/Target 04/Headline Indicators",
  KM_GBF_TARGET_4_COMPONENT = "Policy/Global Biodiversity Framework/Target 04/Component Indicators",
  KM_GBF_TARGET_4_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 04/Complementary Indicators",

  KM_GBF_TARGET_5 = "Policy/Global Biodiversity Framework/Target 05",
  KM_GBF_TARGET_5_HEADLINE = "Policy/Global Biodiversity Framework/Target 05/Headline Indicators",
  KM_GBF_TARGET_5_COMPONENT = "Policy/Global Biodiversity Framework/Target 05/Component Indicators",
  KM_GBF_TARGET_5_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 05/Complementary Indicators",

  KM_GBF_TARGET_6 = "Policy/Global Biodiversity Framework/Target 06",
  KM_GBF_TARGET_6_HEADLINE = "Policy/Global Biodiversity Framework/Target 06/Headline Indicators",
  KM_GBF_TARGET_6_COMPONENT = "Policy/Global Biodiversity Framework/Target 06/Component Indicators",
  KM_GBF_TARGET_6_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 06/Complementary Indicators",

  KM_GBF_TARGET_7 = "Policy/Global Biodiversity Framework/Target 07",
  KM_GBF_TARGET_7_HEADLINE = "Policy/Global Biodiversity Framework/Target 07/Headline Indicators",
  KM_GBF_TARGET_7_COMPONENT = "Policy/Global Biodiversity Framework/Target 07/Component Indicators",
  KM_GBF_TARGET_7_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 07/Complementary Indicators",

  KM_GBF_TARGET_8 = "Policy/Global Biodiversity Framework/Target 08",
  KM_GBF_TARGET_8_HEADLINE = "Policy/Global Biodiversity Framework/Target 08/Headline Indicators",
  KM_GBF_TARGET_8_COMPONENT = "Policy/Global Biodiversity Framework/Target 08/Component Indicators",
  KM_GBF_TARGET_8_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 08/Complementary Indicators",

  KM_GBF_TARGET_9 = "Policy/Global Biodiversity Framework/Target 09",
  KM_GBF_TARGET_9_HEADLINE = "Policy/Global Biodiversity Framework/Target 09/Headline Indicators",
  KM_GBF_TARGET_9_COMPONENT = "Policy/Global Biodiversity Framework/Target 09/Component Indicators",
  KM_GBF_TARGET_9_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 09/Complementary Indicators",

  KM_GBF_TARGET_10 = "Policy/Global Biodiversity Framework/Target 10",
  KM_GBF_TARGET_10_HEADLINE = "Policy/Global Biodiversity Framework/Target 10/Headline Indicators",
  KM_GBF_TARGET_10_COMPONENT = "Policy/Global Biodiversity Framework/Target 10/Component Indicators",
  KM_GBF_TARGET_10_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 10/Complementary Indicators",

  KM_GBF_TARGET_11 = "Policy/Global Biodiversity Framework/Target 11",
  KM_GBF_TARGET_11_HEADLINE = "Policy/Global Biodiversity Framework/Target 11/Headline Indicators",
  KM_GBF_TARGET_11_COMPONENT = "Policy/Global Biodiversity Framework/Target 11/Component Indicators",
  KM_GBF_TARGET_11_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 11/Complementary Indicators",

  KM_GBF_TARGET_12 = "Policy/Global Biodiversity Framework/Target 12",
  KM_GBF_TARGET_12_HEADLINE = "Policy/Global Biodiversity Framework/Target 12/Headline Indicators",
  KM_GBF_TARGET_12_COMPONENT = "Policy/Global Biodiversity Framework/Target 12/Component Indicators",
  KM_GBF_TARGET_12_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 12/Complementary Indicators",

  KM_GBF_TARGET_13 = "Policy/Global Biodiversity Framework/Target 13",
  KM_GBF_TARGET_13_HEADLINE = "Policy/Global Biodiversity Framework/Target 13/Headline Indicators",
  KM_GBF_TARGET_13_COMPONENT = "Policy/Global Biodiversity Framework/Target 13/Component Indicators",
  KM_GBF_TARGET_13_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 13/Complementary Indicators",

  KM_GBF_TARGET_14 = "Policy/Global Biodiversity Framework/Target 14",
  KM_GBF_TARGET_14_HEADLINE = "Policy/Global Biodiversity Framework/Target 14/Headline Indicators",
  KM_GBF_TARGET_14_COMPONENT = "Policy/Global Biodiversity Framework/Target 14/Component Indicators",
  KM_GBF_TARGET_14_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 14/Complementary Indicators",

  KM_GBF_TARGET_15 = "Policy/Global Biodiversity Framework/Target 15",
  KM_GBF_TARGET_15_HEADLINE = "Policy/Global Biodiversity Framework/Target 15/Headline Indicators",
  KM_GBF_TARGET_15_COMPONENT = "Policy/Global Biodiversity Framework/Target 15/Component Indicators",
  KM_GBF_TARGET_15_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 15/Complementary Indicators",

  KM_GBF_TARGET_16 = "Policy/Global Biodiversity Framework/Target 16",
  KM_GBF_TARGET_16_HEADLINE = "Policy/Global Biodiversity Framework/Target 16/Headline Indicators",
  KM_GBF_TARGET_16_COMPONENT = "Policy/Global Biodiversity Framework/Target 16/Component Indicators",
  KM_GBF_TARGET_16_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 16/Complementary Indicators",

  KM_GBF_TARGET_17 = "Policy/Global Biodiversity Framework/Target 17",
  KM_GBF_TARGET_17_HEADLINE = "Policy/Global Biodiversity Framework/Target 17/Headline Indicators",
  KM_GBF_TARGET_17_COMPONENT = "Policy/Global Biodiversity Framework/Target 17/Component Indicators",
  KM_GBF_TARGET_17_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 17/Complementary Indicators",

  KM_GBF_TARGET_18 = "Policy/Global Biodiversity Framework/Target 18",
  KM_GBF_TARGET_18_HEADLINE = "Policy/Global Biodiversity Framework/Target 18/Headline Indicators",
  KM_GBF_TARGET_18_COMPONENT = "Policy/Global Biodiversity Framework/Target 18/Component Indicators",
  KM_GBF_TARGET_18_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 18/Complementary Indicators",

  KM_GBF_TARGET_19 = "Policy/Global Biodiversity Framework/Target 19",
  KM_GBF_TARGET_19_HEADLINE = "Policy/Global Biodiversity Framework/Target 19/Headline Indicators",
  KM_GBF_TARGET_19_COMPONENT = "Policy/Global Biodiversity Framework/Target 19/Component Indicators",
  KM_GBF_TARGET_19_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 19/Complementary Indicators",

  KM_GBF_TARGET_20 = "Policy/Global Biodiversity Framework/Target 20",
  KM_GBF_TARGET_20_HEADLINE = "Policy/Global Biodiversity Framework/Target 20/Headline Indicators",
  KM_GBF_TARGET_20_COMPONENT = "Policy/Global Biodiversity Framework/Target 20/Component Indicators",
  KM_GBF_TARGET_20_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 20/Complementary Indicators",

  KM_GBF_TARGET_21 = "Policy/Global Biodiversity Framework/Target 21",
  KM_GBF_TARGET_21_HEADLINE = "Policy/Global Biodiversity Framework/Target 21/Headline Indicators",
  KM_GBF_TARGET_21_COMPONENT = "Policy/Global Biodiversity Framework/Target 21/Component Indicators",
  KM_GBF_TARGET_21_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 21/Complementary Indicators",

  KM_GBF_TARGET_22 = "Policy/Global Biodiversity Framework/Target 22",
  KM_GBF_TARGET_22_HEADLINE = "Policy/Global Biodiversity Framework/Target 22/Headline Indicators",
  KM_GBF_TARGET_22_COMPONENT = "Policy/Global Biodiversity Framework/Target 22/Component Indicators",
  KM_GBF_TARGET_22_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 22/Complementary Indicators",

  KM_GBF_TARGET_23 = "Policy/Global Biodiversity Framework/Target 23",
  KM_GBF_TARGET_23_HEADLINE = "Policy/Global Biodiversity Framework/Target 23/Headline Indicators",
  KM_GBF_TARGET_23_COMPONENT = "Policy/Global Biodiversity Framework/Target 23/Component Indicators",
  KM_GBF_TARGET_23_COMPLEMENTARY = "Policy/Global Biodiversity Framework/Target 23/Complementary Indicators",
}

export enum LayerType {
  raster = "raster",
  vector = "vector",
  group = "group",
}

export enum LayerProviders {
  STAC = "SpatioTemporal Asset Catalog (STAC)",
  GEE = "Google earth engine",
  EXTERNAL = "External Tile Service",
  CARTO = "Carto",
  UPLOAD_FILE = "GeoTIFF File Upload",
}

// Interactive legend control types. Authored in admin (earth-admin) and rendered
// on the map (earth-map), so the enum lives here to keep both sides in sync.
export enum InteractiveControlTypes {
  TAXON_PICKER = "taxonPicker",
  YEAR_RANGE = "yearRange",
  MULTI_SELECT = "multiSelect",
  SELECT = "select",
  ASYNC_SELECT = "asyncSelect",
  ASYNC_MULTI_SELECT = "asyncMultiSelect",
  TOGGLE = "toggle",
  TEXT = "text",
}

// A group layer's child selector (its `legendConfig.legendType`). Authored in
// admin and read by the map.
export enum GroupLegendTypes {
  YEAR_PICKER = "yearpicker",
  YEAR_DATE_PICKER = "yeardatepicker",
  TIMELINE = "timeline",
}

// The key a raster interactive layer's per-form style rides under (in redux + the
// URL) and the token it substitutes into the tile URL (`&style={_style}`).
// Underscore-prefixed so it can't collide with an admin-authored control paramKey.
// Lives here so admin (which authors the tile URL) and the map (which stores /
// substitutes the value) share one definition.
export const STYLE_PARAM = "_style";

// The tile-URL placeholder form of STYLE_PARAM, i.e. `{_style}`.
export const STYLE_PLACEHOLDER = `{${STYLE_PARAM}}`;

// The token an async-select or taxon-picker endpoint URL carries for the user's
// language (`locale={_locale}`). Shared so the admin help text names the token
// the map substitutes.
export const LOCALE_PARAM = "_locale";
export const LOCALE_PLACEHOLDER = `{${LOCALE_PARAM}}`;

/**
 * The layer fields the API returns and both apps read, mirroring `Layer` in
 * unbl-services/src/models/LayerModel.ts. Optionality follows the API; an app
 * can only tighten it (make a field required). `parent` and `references` stay
 * per-app because each refers back to its own ILayer.
 */
export interface ILayerBase {
  // Required by the API on every stored layer
  slug: string;
  name: string;
  description: string;
  type: LayerType;
  category: LayerCategory[];
  tags: string[];
  published: boolean;

  id?: string;
  // Required by the API, but the map fabricates ELSA pseudo-layers without one
  organization?: string;
  provider?: LayerProviders;
  licenseRestriction?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  version?: number;
  sources?: string;
  citation?: string;
  license?: string;
  disclaimer?: string;
  readMoreLinks?: LearnMoreLink[];
  downloadLinks?: LearnMoreLink[];
  extraMetadata?: ExtraMetadata;
  pointOfContact?: PointOfContact;
}

/** The STACConfig fields both apps read on a layer. */
export interface ISTACConfigBase {
  collection?: string;
  asset?: string;
  startDate?: string;
  endDate?: string;
  STAC_API_URL?: string;
  TILER_URL?: string;
  STACLegendItems?: any[];
}
